import React, { useEffect, useMemo, useState } from 'react';
import { FaArrowLeft } from 'react-icons/fa6';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import FluxoAdminEtapas from './components/FluxoAdminEtapas';
import BarraInferiorBotoes from '../../../../../../components/BarraInferiorBotoes';
import BarraSubAbas from '../../../../../../components/BarraSubAbas';
import BoxConteudo from '../../../../../../components/BoxConteudo';
import ContainerConteudo from '../../../../../../components/ContainerConteudo';
import CustomCol from '../../../../../../components/CustomCol';
import CustomInputLink from '../../../../../../components/CustomInputs/CustomInputLink';
import CustomInputText from '../../../../../../components/CustomInputs/CustomInputText';
import CustomRow from '../../../../../../components/CustomRow';
import CustomSelect from '../../../../../../components/CustomSelects/CustomSelect';
import StyledLink from '../../../../../../components/Link';
import Log from '../../../../../../components/Log/Log';
import SubAbas from '../../../../../../components/SubAbas';
import WorkspaceBotoes from '../../../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../../../components/WorkspaceFicha';
import { useSelect } from '../../../../../../modules/contexts/Select/useSelect';
import {
  IFluxoAdminModeloForm,
  IFluxoAdminModeloRegistro,
} from '../../../../../../modules/types/FluxoAdmin';
import { ISetorDafRegistro } from '../../../../../../modules/types/SetorDaf';
import { getFluxoAdminModeloById } from '../../../../../../services/fluxosAdmin/fluxoAdminModeloService';
import { getAllSetoresDaf } from '../../../../../../services/setoresDaf/setoresDafService';
import { formataNomeMeioSiglas } from '../../../../../../utils/functions';
import { handleDelete } from '../../../../../../utils/handleDelete';
import { handleSave } from '../../../../../../utils/handleSave';
import { optionStatusBin } from '../../../Dominios/utils/optionStatusBin';
import SubAbasFluxosAdmin from '../../utils/subabasFluxosAdmin';

const defaultForm: IFluxoAdminModeloForm = {
  id: 0,
  setorId: 0,
  dominioId: 0,
  dominioTipoId: null,
  dominioSubtipoId: null,
  status: 0,
  nomeModelo: '',
  descricao: '',
  linkMaisInfo: '',
  observacao: '',
};

export default function FluxoAdminModeloIdentificacao() {
  const params = useParams();
  const fichaID = String(params.id);
  const theme = useTheme();

  const { getSelectDominiosRaiz, getSelectDominioPai } = useSelect();
  const [setores, setSetores] = useState<ISetorDafRegistro[]>([]);

  const [logData, setLogData] = useState<IFluxoAdminModeloRegistro | null>(
    null,
  );
  const [originalForm, setOriginalForm] =
    useState<IFluxoAdminModeloForm | null>(null);
  const [logRefresh, setLogRefresh] = useState(0);

  const [formMain, setFormMain] = useState<IFluxoAdminModeloForm>(defaultForm);

  const mapRegistroToForm = (
    registro: IFluxoAdminModeloRegistro,
  ): IFluxoAdminModeloForm => ({
    id: registro.id,
    setorId: registro.setorId,
    dominioId: registro.dominioId ?? 0,
    dominioTipoId: registro.dominioTipoId ?? null,
    dominioSubtipoId: registro.dominioSubtipoId ?? null,
    status: registro.status,
    nomeModelo: registro.nomeModelo,
    descricao: registro.descricao,
    linkMaisInfo: registro.linkMaisInfo || '',
    observacao: registro.observacao || '',
  });

  const fetchData = async (id: string | number): Promise<void> => {
    try {
      const registroData: IFluxoAdminModeloRegistro =
        await getFluxoAdminModeloById(String(id));

      setLogData(registroData);

      const parsedForm = mapRegistroToForm(registroData);
      setFormMain(parsedForm);
      setOriginalForm(parsedForm);
      setLogRefresh((prev) => prev + 1);
    } catch (error) {
      console.error('Erro ao obter dados do fluxo administrativo:', error);
    }
  };

  useEffect(() => {
    if (fichaID !== 'undefined') {
      fetchData(fichaID);
    }
  }, [fichaID]);

  useEffect(() => {
    const loadSetores = async () => {
      try {
        const lista = await getAllSetoresDaf();
        setSetores(lista);
      } catch (error) {
        console.error('Erro ao carregar setores:', error);
        setSetores([]);
      }
    };

    loadSetores();
  }, []);

  const subAbas =
    fichaID !== 'undefined'
      ? SubAbasFluxosAdmin(fichaID)
      : [
          {
            label: 'Dados Gerais',
            rota: `/daf/adm/fluxos-adm/modelo/dadosgerais`,
          },
        ];

  const logs = logData
    ? {
        id: logData.id,
        usuarioRegistro: formataNomeMeioSiglas(
          logData.usuarioRegistro.nomeCompleto,
        ),
        usuarioAtualizacao: formataNomeMeioSiglas(
          logData.usuarioAtualizacao.nomeCompleto,
        ),
        dataRegistro: logData.dataRegistro,
        dataAtualizacao: logData.dataUltimaAtualizacao,
        nEdicoes: logData.nEdicoes,
      }
    : null;

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;
    if (name === 'dominioId') {
      const dominioValue = Number(value);
      setFormMain((prev) => ({
        ...prev,
        dominioId: dominioValue,
        dominioTipoId: null,
        dominioSubtipoId: null,
      }));
      return;
    }

    if (name === 'dominioTipoId') {
      const dominioTipoValue =
        value === '0' || value === '' ? null : Number(value);
      setFormMain((prev) => ({
        ...prev,
        dominioTipoId: dominioTipoValue,
        dominioSubtipoId: null,
      }));
      return;
    }

    setFormMain((prevState) => ({
      ...prevState,
      [name]:
        name === 'status' || name === 'setorId'
          ? Number(value)
          : name === 'dominioSubtipoId'
            ? value === '0' || value === ''
              ? null
              : Number(value)
            : value,
    }));
  };

  const camposObrigatorios = {
    status: 'Status do Modelo',
    nomeModelo: 'Nome do Modelo',
    descricao: 'Descrição',
    setorId: 'Setor Responsável',
    dominioId: 'Domínio',
  };

  const camposAjustar = {
    number: [
      'id',
      'status',
      'setorId',
      'dominioId',
      'dominioTipoId',
      'dominioSubtipoId',
    ],
    string: ['nomeModelo', 'descricao', 'observacao', 'linkMaisInfo'],
  };

  const handleSaveWrapper = async () => {
    const saved = await handleSave(
      formMain,
      camposObrigatorios,
      camposAjustar,
      'fluxoadmmodelos',
      'daf/adm/fluxos-adm/modelo',
      undefined,
      true,
      originalForm || undefined,
    );

    if (saved && fichaID !== 'undefined') {
      await fetchData(fichaID);
    }
  };

  const handleDeleteWrapper = () =>
    handleDelete(fichaID, 'fluxoadmmodelos', 'daf/adm/fluxos-adm');

  const setorOptions = useMemo(
    () =>
      setores.map((item) => ({
        value: item.id,
        label: `${item.sigla} - ${item.nomeSetorDaf}`,
      })),
    [setores],
  );

  const dominioRaizOptions = useMemo(
    () => getSelectDominiosRaiz(),
    [getSelectDominiosRaiz],
  );

  const dominioTipoOptions = useMemo(() => {
    return getSelectDominioPai(formMain.dominioId ?? 0);
  }, [formMain.dominioId, getSelectDominioPai]);

  const dominioSubtipoOptions = useMemo(
    () => getSelectDominioPai(formMain.dominioTipoId ?? 0),
    [formMain.dominioTipoId, getSelectDominioPai],
  );

  const hasTipoOptions = dominioTipoOptions.length > 1;
  const hasSubtipoOptions = dominioSubtipoOptions.length > 1;

  const isFichaCriada = fichaID !== 'undefined' && Number(fichaID) > 0;

  return (
    <>
      <SubAbas>
        <StyledLink
          to="/daf/adm/fluxos-adm"
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <FaArrowLeft />
        </StyledLink>
        <BarraSubAbas subAbas={subAbas} />
      </SubAbas>

      <ContainerConteudo>
        <WorkspaceFicha>
          {logs && (
            <Log
              {...logs}
              entity="TB_FLUXO_ADM_MODELO"
              refreshTriggerLog={logRefresh}
            />
          )}

          <BoxConteudo boxTitulo="Identificação do Modelo">
            <CustomRow
              style={{ marginTop: '-15px', justifyContent: 'flex-start' }}
            >
              <CustomCol width="200px">
                <CustomSelect
                  name="status"
                  options={optionStatusBin}
                  title="Status"
                  value={formMain.status}
                  onChange={handleChange}
                  style={{
                    backgroundColor:
                      Number(formMain.status) === 2
                        ? theme.colors.vermelho
                        : Number(formMain.status) === 1
                          ? theme.colors.verde
                          : 'white',
                    color:
                      Number(formMain.status) === 0 ? '#000000' : '#ffffff',
                  }}
                  required
                />
              </CustomCol>

              <CustomCol width="calc(100% - 200px)">
                <CustomInputText
                  name="nomeModelo"
                  title="Modelo"
                  value={formMain.nomeModelo}
                  onChange={handleChange}
                  maxLength={255}
                  required
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <CustomCol width="280px">
                <CustomSelect
                  name="setorId"
                  options={[
                    { value: 0, label: 'Não informado' },
                    ...setorOptions,
                  ]}
                  title="Setor DAF"
                  value={formMain.setorId}
                  onChange={handleChange}
                  required
                />
              </CustomCol>

              <CustomCol width="260px">
                <CustomSelect
                  name="dominioId"
                  options={dominioRaizOptions}
                  title="Domínio"
                  value={formMain.dominioId ?? 0}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol width="260px">
                <CustomSelect
                  name="dominioTipoId"
                  options={
                    formMain.dominioId
                      ? dominioTipoOptions
                      : [{ value: 0, label: 'Não informado' }]
                  }
                  title="Tipo de Domínio"
                  value={formMain.dominioTipoId ?? 0}
                  onChange={handleChange}
                  disabled={!formMain.dominioId || !hasTipoOptions}
                />
              </CustomCol>
              <CustomCol width="260px">
                <CustomSelect
                  name="dominioSubtipoId"
                  options={
                    formMain.dominioTipoId
                      ? dominioSubtipoOptions
                      : [{ value: 0, label: 'Não informado' }]
                  }
                  title="Subtipo de Domínio"
                  value={formMain.dominioSubtipoId ?? 0}
                  onChange={handleChange}
                  disabled={!formMain.dominioTipoId || !hasSubtipoOptions}
                />
              </CustomCol>
              <CustomCol />
            </CustomRow>

            <CustomRow>
              <CustomCol width="100%">
                <CustomInputLink
                  name="linkMaisInfo"
                  title="Link para mais informações"
                  value={formMain.linkMaisInfo}
                  onChange={handleChange}
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <CustomCol width="100%">
                <CustomInputText
                  name="descricao"
                  title="Descrição"
                  value={formMain.descricao}
                  onChange={handleChange}
                  type="textArea"
                  maxLength={800}
                  required
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <CustomCol width="100%">
                <CustomInputText
                  name="observacao"
                  title="Observações"
                  type="textArea"
                  value={formMain.observacao}
                  onChange={handleChange}
                  maxLength={255}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>

          {isFichaCriada && (
            <FluxoAdminEtapas
              modeloId={Number(fichaID)}
              onLogsRefresh={() => setLogRefresh((prev) => prev + 1)}
            />
          )}
        </WorkspaceFicha>
      </ContainerConteudo>

      <BarraInferiorBotoes>
        <WorkspaceBotoes
          save
          onClickSave={handleSaveWrapper}
          delete={isFichaCriada}
          onClickDelete={handleDeleteWrapper}
        />
      </BarraInferiorBotoes>
    </>
  );
}
